import { sessionTime } from '@/lib/session-labels';
import type {
  ReplayFilter,
  ReplayMode,
  ReplayOrigin,
  ReplaySourceKind,
} from '@matter_extractor/shared';

/**
 * What the screens of Sessions say of a REPLAY (rejeu.md section 12, issue
 * #193) - pure, in the voice of the maquettes `Liste` and `Parcours`: the pill,
 * the sentence of each mode, the words for a source, the tenth filter.
 *
 * A file of its own, like `review-entry.ts` for the review: the list, the
 * parcours and the conversation all need the same four or five words, and a
 * label spelt in two places is a label that drifts (« complété » on the list,
 * « completé » on the head).
 *
 * The enumerations (`ReplayMode`, `ReplaySourceKind`, `ReplayFilter`) are the
 * contract's, in `shared`: this file words them and decides nothing.
 */

/* ------------------------------------------------------------------ the pill */

/** The mode as the pill and the panel say it: « strict », « complété ». */
export const REPLAY_MODE_LABEL: Readonly<Record<ReplayMode, string>> = {
  strict: 'strict',
  completed: 'complété',
};

/** « rejeu · strict », « rejeu · complété » - the pill of the list, the parcours and the conversation. */
export function replayPillLabel(mode: ReplayMode): string {
  return `rejeu · ${REPLAY_MODE_LABEL[mode]}`;
}

/** The sentence under the mode in the panel (§12): what the player was allowed. */
export const REPLAY_MODE_SENTENCE: Readonly<Record<ReplayMode, string>> = {
  strict: 'rien hors de la source',
  completed: 'complété dans l’esprit de la source',
};

/* ---------------------------------------------------------------- the source */

/** What a source is, with its article: « un écrit », « une session », « un entretien ». */
export const REPLAY_SOURCE_KIND_LABEL: Readonly<Record<ReplaySourceKind, string>> = {
  written: 'un écrit',
  session: 'une session',
  interview: 'un entretien',
};

/** `2 400 caractères`, grouped with the thin space France groups thousands with; `1 caractère` in the singular. */
export function replaySourceSize(chars: number): string {
  const grouped = String(Math.round(chars)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped} ${chars > 1 ? 'caractères' : 'caractère'}`;
}

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avril',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/** `14 oct. 2026 à 15:02`, written by hand: `Intl` answers by the locale of the MACHINE. */
export function replayStartedLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = MONTHS_SHORT[date.getMonth()] ?? '';
  return `${date.getDate()} ${month} ${date.getFullYear()} à ${sessionTime(iso)}`;
}

/* ------------------------------------------------------------- the origin */

/**
 * The fact « Rejoue » of the panel: the replay this one replayed.
 *
 * Three shapes. No origin: « aucun ». An origin still readable (its experience
 * version is known): a link, and « même source, sur la v20 ». An origin gone
 * (purged with its session, wiped by a refresh of the dev model, so its version
 * is null): the words and no link, because the link would lead to « Aucune
 * session sous cet identifiant ».
 */
export interface ReplayOriginFact {
  readonly kind: 'none' | 'linked' | 'gone';
  /** The words of the fact: « le rejeu 7c1e4a90 », « un rejeu effacé », « aucun ». */
  readonly label: string;
  /** « même source, sur la v20 », or « même source » when the version is not known; null on none. */
  readonly detail: string | null;
  readonly replayId: string | null;
}

/** The 8 first characters of an identifier: how an operator names a replay aloud. */
export function shortReplayId(id: string): string {
  return id.slice(0, 8);
}

export function replayOriginFact(origin: ReplayOrigin | null): ReplayOriginFact {
  if (origin === null) return { kind: 'none', label: 'aucun', detail: null, replayId: null };
  if (origin.experienceVersionNumber === null) {
    return {
      kind: 'gone',
      label: 'un rejeu effacé',
      detail: 'même source',
      replayId: origin.replayId,
    };
  }
  return {
    kind: 'linked',
    label: `le rejeu ${shortReplayId(origin.replayId)}`,
    detail: `même source, sur la v${origin.experienceVersionNumber}`,
    replayId: origin.replayId,
  };
}

/* ------------------------------------------------------------- the head */

/**
 * The subtitle of a replay's head (§12): « rejouée par Claude, sans lien
 * d'entrée » where an ordinary session says « entrée par le lien ... ». The
 * bold part is `REPLAY_HEAD_LEAD`, the rest `REPLAY_HEAD_TAIL`.
 */
export const REPLAY_HEAD_LEAD = 'rejouée par Claude';
export const REPLAY_HEAD_TAIL = ', sans lien d’entrée';

/* ------------------------------------------------------------ the filter */

/** The chip of the tenth filter once armed: « Rejeu : avec ». */
export function replayFilterChipLabel(filter: ReplayFilter): string {
  return `Rejeu : ${filter === 'with' ? 'avec' : 'sans'}`;
}

/** What the selector offers, in the voice of the maquette `Liste`. */
export const REPLAY_FILTER_OPTION_LABEL: Readonly<Record<ReplayFilter, string>> = {
  with: 'Les rejeux seulement',
  without: 'Sans les rejeux',
};

/** The sentence under the panel of « Plus de filtres » (§12). */
export const REPLAY_FILTER_HELP =
  '« Rejeu » retient les sessions jouées par Claude à la place d’une personne, depuis l’assistant MCP de ce serveur.';

/** What the address bar and the server spell. */
export function isReplayFilter(value: unknown): value is ReplayFilter {
  return value === 'with' || value === 'without';
}

/* ------------------------------------------------------------ the sentence */

/** The sentence « Pour rejouer ce cas ... » of the panel, up to the identifier. */
export const REPLAY_AGAIN_LEAD =
  'Pour rejouer ce cas sur la version suivante, dire à Claude : « rejoue ce rejeu », avec l’identifiant';
